import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';

import { ConfigService } from './config.service';
import {OpenBankingOverview} from "../models/open-banking/open-banking-overview";
import {NewSession} from "../models/open-banking/new-session";
import {OpenBankingConnection} from "../models/open-banking/open-banking-connection";
import {TransactionSync} from "../models/open-banking/transaction-sync";
import {TransactionsReport} from "../models/open-banking/transactions-report";
import {PatchConnectionAccountConfigModel} from "../models/open-banking/patch-connection-account-config-model";
import {BankFeedHealthCheck} from "../models/open-banking/bank-feed-health-check";
import {ManageOrganisationService} from './manage-organisation.service';
import {tap} from 'rxjs/operators';

@Injectable()
export class OpenBankingService {

    onOverviewReloaded = new Subject<{ organisationReference: string, overview: OpenBankingOverview }>()

    constructor(private http: HttpClient, private configService: ConfigService, private manageOrganisationService: ManageOrganisationService) {
    }

    getOverview(organisationReference: string): Observable<OpenBankingOverview> {
        const url = `${this.configService.baseUrl}/manage/${organisationReference}/open-banking/overview`;
        return this.http.get<OpenBankingOverview>(url).pipe(
            tap(overview => {
                this.onOverviewReloaded.next({
                    organisationReference,
                    overview
                });
            })
        );
    }

    addSession(): Observable<NewSession> {
        return this.http.get<NewSession>(`${this.configService.baseUrl}/open-banking/session`);
    }

    getConnections(includeDeleted: boolean = false): Observable<OpenBankingConnection[]> {
        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        return this.http.get<OpenBankingConnection[]>(`${this.configService.baseUrl}/manage/${organisationReference}/open-banking/connections`, {
            params: {
                includeDeleted
            }
        });
    }

    getConnection(connectionGuid: string) {
        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        return this.http.get<OpenBankingConnection>(`${this.configService.baseUrl}/manage/${organisationReference}/open-banking/connections/${connectionGuid}`);
    }

    updateConnection(connectionGuid: string) {
        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        return this.http.post<void>(`${this.configService.baseUrl}/manage/${organisationReference}/open-banking/connections/${connectionGuid}/update`, null);
    }

    reconnectConnection(connectionGuid: string): Observable<NewSession> {
        return this.http.get<NewSession>(`${this.configService.baseUrl}/open-banking/connections/${connectionGuid}/reconnect`);
    }

    refreshConnection(connectionGuid: string): Observable<NewSession> {
        return this.http.get<NewSession>(`${this.configService.baseUrl}/open-banking/connections/${connectionGuid}/refresh`);
    }

    deleteConnection(connectionGuid: string): Observable<void> {
        const organisationReference = this.manageOrganisationService.getOrganisationReference();
        return this.http.delete<void>(`${this.configService.baseUrl}/manage/${organisationReference}/open-banking/connections/${connectionGuid}`);
    }

    deleteAccount(connectionGuid: string, accountGuid: string): Observable<void> {
        return this.http.delete<void>(`${this.configService.baseUrl}/open-banking/connections/${connectionGuid}/accounts/${accountGuid}`);
    }

    syncTransactions(organisationReference: string, connectionGuid: string): Observable<TransactionSync> {
        return this.http.get<TransactionSync>(`${this.configService.baseUrl}/manage/${organisationReference}/open-banking/connections/${connectionGuid}/sync`);
    }

    getTransactionReport(): Observable<TransactionsReport> {
        return this.http.get<TransactionsReport>(`${this.configService.baseUrl}/open-banking/report/`);
    }

    agreeTerms(): Observable<void> {
        return this.http.post<void>(`${this.configService.baseUrl}/open-banking/terms/`, true);
    }

    dismissConnectMessage(): Observable<void> {
        return this.http.post<void>(`${this.configService.baseUrl}/open-banking/dismiss-connect/`, true);
    }

    setAccountFriendlyName(connectionGuid: string, accountGuid: string, friendlyName: string): Observable<void> {
        return this.http.post<void>(`${this.configService.baseUrl}/open-banking/connections/${connectionGuid}/accounts/${accountGuid}/friendly-name/${friendlyName}`, null)
    }

    patchConnectionAccountConfig(organisationReference: string, connectionGuid: string, accountGuid: string, model: PatchConnectionAccountConfigModel) {
        const url = `${this.configService.baseUrl}/manage/${organisationReference}/open-banking/connections/${connectionGuid}/accounts/${accountGuid}/config`
        return this.http.patch(url, model)
    }

    getBankFeedHealthCheck(organisationReference: string, connectionGuid: string, accountGuid: string) {
        return this.http.get<BankFeedHealthCheck>(`${this.configService.baseUrl}/manage/${organisationReference}/open-banking/connections/${connectionGuid}/accounts/${accountGuid}/health-check`);
    }

    importMissingCustomerConnections(organisationReference: string) {
        const url = `${this.configService.baseUrl}/manage/${organisationReference}/open-banking/import-missing-connections`
        return this.http.post<void>(url, null);
    }
}
